import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { FinanceChannelFieldAvailability } from '@/api/generated/model';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { NotReadyBadge, NotReadyInline, NotReadyNote } from './FinanceNotReady';

export type RiskMetricView = {
  label: string;
  value: number | null;
  availability: FinanceChannelFieldAvailability;
  display?: string;
};

/** Confluence §6 Risk Overview — Under Review live; others shell. */
export function RiskOverviewCard({
  metrics,
}: {
  metrics?: {
    highRisk: RiskMetricView;
    critical: RiskMetricView;
    underReview: RiskMetricView;
    fraudRate: RiskMetricView;
  };
}) {
  const navigate = useNavigate();
  const rows = metrics
    ? [metrics.highRisk, metrics.critical, metrics.underReview, metrics.fraudRate]
    : [
        { label: 'High Risk', value: null, availability: 'COMING_SOON' as const },
        { label: 'Critical Alerts', value: null, availability: 'COMING_SOON' as const },
        { label: 'Under Review', value: null, availability: 'COMING_SOON' as const },
        { label: 'Fraud Rate', value: null, availability: 'COMING_SOON' as const },
      ];

  const liveCount = rows.filter(
    (row) => row.availability === 'AVAILABLE' && row.value != null,
  ).length;
  const headerState =
    liveCount === 0 ? 'not-ready' : liveCount < rows.length ? 'partial' : 'live';

  return (
    <Card className="overflow-hidden bg-card/80 shadow-none">
      <CardHeader className="space-y-1 p-4 pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium text-sky-400">Risk Overview</CardTitle>
          <NotReadyBadge
            state={headerState}
            reason={
              headerState === 'partial'
                ? 'Only Accounts Under Review is live from Moderation. High Risk, Critical Alerts, and Fraud Rate are not ready yet.'
                : 'Risk metrics are not ready yet. Open Moderation for account review workflows.'
            }
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          High-risk cases, alerts, accounts under review, fraud rate
        </p>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-1">
        <div className="grid grid-cols-2 gap-2">
          {rows.map((item) => {
            const live = item.availability === 'AVAILABLE' && item.value != null;
            const text = item.display ?? (live ? String(item.value) : '—');
            return (
              <div
                key={item.label}
                className="rounded-md border border-border/50 bg-background/40 px-2.5 py-2"
              >
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {item.label}
                  {!live ? (
                    <NotReadyInline
                      className="ml-1"
                      reason={
                        item.label.toLowerCase().includes('review')
                          ? 'Moderation metrics are unavailable right now.'
                          : item.label.toLowerCase().includes('fraud')
                            ? 'Not ready — fraud rate needs a defined fraud signal. Never invent a percentage.'
                            : 'Not ready — product has not defined this risk source yet.'
                      }
                    />
                  ) : null}
                </p>
                <p
                  className={`mt-1 text-sm font-semibold tabular-nums ${
                    live ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {text}
                </p>
              </div>
            );
          })}
        </div>
        {headerState !== 'live' ? (
          <NotReadyNote>
            {headerState === 'partial'
              ? 'Only Accounts Under Review is live from Moderation. High Risk, Critical Alerts, and Fraud Rate are not ready yet.'
              : 'Risk metrics are not ready yet. Open Moderation for account review workflows.'}
          </NotReadyNote>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => navigate(ROUTES.moderation)}
        >
          <AlertTriangle className="size-3.5" aria-hidden />
          Open Moderation
        </Button>
      </CardContent>
    </Card>
  );
}
